import React, { useState } from 'react';
import { User, Calendar, Download, ChevronLeft, ChevronRight, FileText, CheckCircle } from 'lucide-react';
import api from '../../../utils/api';
import toast from 'react-hot-toast';

const QAView = ({ instances, survey }) => {
    const [selectedInstanceIndex, setSelectedInstanceIndex] = useState(0);
    const [exporting, setExporting] = useState(false);

    if (instances.length === 0) {
        return (
            <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Completed Responses</h3>
                <p className="text-gray-500">Q&A format will appear here once employees complete the survey.</p>
            </div>
        );
    }

    const currentInstance = instances[selectedInstanceIndex];

    const handleExportEmployee = async (format = 'csv') => {
        try {
            setExporting(true);
            const response = await api.get(`/surveys/employee/${currentInstance.id}/export`, {
                responseType: 'blob',
                params: { format }
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const employeeName = currentInstance.employee_name?.replace(/\s+/g, '_') || `employee_${currentInstance.employee_id}`;
            link.setAttribute('download', `survey_responses_${employeeName}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success(`Export downloaded as ${format.toUpperCase()}!`);
        } catch (error) {
            console.error("Export failed", error);
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const goToPrevious = () => {
        setSelectedInstanceIndex((prev) => (prev > 0 ? prev - 1 : instances.length - 1));
    };

    const goToNext = () => {
        setSelectedInstanceIndex((prev) => (prev < instances.length - 1 ? prev + 1 : 0));
    };

    return (
        <div className="space-y-4">
            {/* Employee Navigation */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                            <User className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                {currentInstance.employee_name || `Employee #${currentInstance.employee_id}`}
                            </h3>
                            <p className="text-sm text-gray-600">{currentInstance.employee_email}</p>
                            <div className="flex items-center gap-2 mt-1">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <span className="text-xs text-gray-500">
                                    Completed: {formatDate(currentInstance.completed_at)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {/* Navigation */}
                        <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200">
                            <button
                                onClick={goToPrevious}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                title="Previous employee"
                            >
                                <ChevronLeft size={18} className="text-gray-600" />
                            </button>
                            <span className="text-sm font-medium text-gray-700 px-2">
                                {selectedInstanceIndex + 1} / {instances.length}
                            </span>
                            <button
                                onClick={goToNext}
                                className="p-1 hover:bg-gray-100 rounded transition-colors"
                                title="Next employee"
                            >
                                <ChevronRight size={18} className="text-gray-600" />
                            </button>
                        </div>

                        {/* Export Buttons */}
                        <button
                            onClick={() => handleExportEmployee('csv')}
                            disabled={exporting}
                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            <Download size={14} />
                            CSV
                        </button>
                        <button
                            onClick={() => handleExportEmployee('pdf')}
                            disabled={exporting}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
                        >
                            <Download size={14} />
                            PDF
                        </button>
                    </div>
                </div>
            </div>

            {/* Q&A List */}
            <div className="space-y-4">
                {currentInstance.responses && currentInstance.responses.length > 0 ? (
                    currentInstance.responses.map((response, index) => (
                        <div
                            key={response.id || index}
                            className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-sm transition-shadow"
                        >
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center font-bold text-sm">
                                    {index + 1}
                                </div>
                                <div className="flex-1 space-y-3">
                                    {/* Question */}
                                    <div>
                                        <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-1">
                                            Question
                                        </p>
                                        <p className="text-gray-900 font-medium leading-relaxed">
                                            {response.snapshot_question_text || response.question_text || 'Question not available'}
                                        </p>
                                    </div>

                                    {/* Answer */}
                                    <div>
                                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">
                                            Answer
                                        </p>
                                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                            {response.skipped ? (
                                                <p className="text-gray-400 italic">Skipped</p>
                                            ) : (
                                                <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                                                    {response.response_text || response.response_value || 'No response'}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Sentiment Badge (if available) */}
                                    {response.sentiment_label && (
                                        <div className="flex items-center gap-2">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                                                response.sentiment_label === 'Positive' ? 'bg-green-100 text-green-700' :
                                                response.sentiment_label === 'Negative' ? 'bg-red-100 text-red-700' :
                                                'bg-gray-100 text-gray-700'
                                            }`}>
                                                {response.sentiment_label === 'Positive' && <CheckCircle size={12} />}
                                                Sentiment: {response.sentiment_label}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-8 text-gray-500">
                        No responses available for this employee.
                    </div>
                )}
            </div>

            {/* Employee List for Quick Jump */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Jump to Employee</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {instances.map((instance, index) => (
                        <button
                            key={instance.id}
                            onClick={() => setSelectedInstanceIndex(index)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                index === selectedInstanceIndex
                                    ? 'bg-indigo-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {instance.employee_name?.split(' ')[0] || `Employee ${index + 1}`}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default QAView;
